import React, {useCallback, useContext, useState} from 'react';

import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {useShown} from '@pb/kit-hooks-use-shown';


import {useDeleteAccount} from './useDeleteAccount';

type Props = {
    accountId: number,
    onDone: () => void,
};

const DELETE_ACCOUNT_MODAL_WIDTH = 420;

export const DeleteAccount = ({accountId, onDone}: Props) => {
    const {shown, onClick, onClose} = useShown();
    const {setSuccessMessage} = useContext(MessageContext);
    const {canDeleteAccount} = useAdminAccessRight();

    const onSuccess = useCallback(() => {
        setSuccessMessage('Account is successfully deleted');
        onClose();
        onDone();
    }, [onDone, setSuccessMessage, onClose]);

    return (
        <>
            {canDeleteAccount(accountId) ? (
                <Button variant="Type.SECONDARY" colorVariant="ERROR" onClick={onClick}>
                    Delete
                </Button>
            ) : null}
            <Dialog
                shown={shown}
                setShown={onClose}
                wrapperProps={{style: {width: DELETE_ACCOUNT_MODAL_WIDTH}}}>
                <DeleteAccountModal accountId={accountId} onSuccess={onSuccess} onClose={onClose}/>
            </Dialog>
        </>
    );
};

type ModalProps = {
    accountId: number,
    onClose: () => void,
    onSuccess: () => void,
};

const DeleteAccountModal = ({accountId, onSuccess, onClose}: ModalProps) => {
    const [errorMessage, setErrorMessage] = useState('');
    const {event, busy} = useDeleteAccount({accountId, onSuccess, onFail: setErrorMessage});

    const onDeleteClick = useCallback(() => {
        event();
    }, [event]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Delete Account
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {errorMessage ? <Message color="error">{errorMessage}</Message> : null}
                Are you sure you want to delete this account?

                Note: Account can be restored.
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.PRIMARY" busy={busy} onClick={onDeleteClick}>
                        Delete
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
